import { Platform } from '@angular/cdk/platform';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Track } from '@app/core/models/Track';
import { HotkeysService } from '@app/core/services/hotkeys.service';
import { PlayAudio } from '@app/core/states/audio.actions';
import { AudioState } from '@app/core/states/audio.state';
import { ArchivedTrack } from '@app/library/models/trash.model';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { ResetEditingTracks } from '@app/library/states/editing-track.actions';
import {
  SelectArchivedTrack,
  SelectMultipleArchivedTracks,
  ToggleSelectArchivedTrack,
  UnselectAll,
  UnselectArchivedTrack,
} from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { ArchivedTrackRestoreRequest } from '@app/library/states/trash.actions';
import { TrashState } from '@app/library/states/trash.state';
import { MIXPOINT_MODAL_ID } from '@app/shared/constants';
import {
  sortDate,
  sortDateReverse,
  sortNumber,
  sortNumberReverse,
  sortString,
  sortStringReverse,
} from '@app/shared/utils';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import amplitude from 'amplitude-js';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { map, take, throttleTime } from 'rxjs/operators';

import { ColumnShown } from '../../../models/column-shown.model';

export type SortPredicate =
  | 'title'
  | 'artist'
  | 'album'
  | 'timeSeconds'
  | 'uploadDate'
  | 'archivedAt'
  | 'bitrate'
  | 'bpm'
  | 'none';

@Component({
  selector: 'rk-trash-audio-table',
  templateUrl: './trash-audio-table.component.html',
  styleUrls: ['./trash-audio-table.component.scss'],
})
export class TrashAudioTableComponent implements AfterViewInit, OnDestroy {
  @Input()
  set archivedTracks(archivedTracksInput: ArchivedTrack[]) {
    if (archivedTracksInput) {
      this._originalArchivedTracks = archivedTracksInput;
      this.archivedTracksObservable$.next(archivedTracksInput);
      this.sortAll();
      this.detectScroll();
    }
  }

  get archivedTracks(): ArchivedTrack[] {
    return this._originalArchivedTracks || [];
  }

  _originalArchivedTracks: ArchivedTrack[];

  @Input()
  trashLength: number;

  @Input()
  columns: ColumnShown;

  @Select(SelectedTracksState.selectedArchivedTracks)
  selectedArchivedTracks$: Observable<number[]>;

  @Select(SelectedTracksState.numberArchivedTracksSelected)
  numberArchivedTracksSelected$: Observable<number>;

  @Select(AudioState.currentTrackPlaying)
  currentTrackPlaying$: Observable<Track>;

  @Select(AudioState.isPlaying)
  isPlaying$: Observable<boolean>;

  @Input()
  searchField$: Observable<string>;

  @ViewChild(CdkVirtualScrollViewport) cdkTable: CdkVirtualScrollViewport;
  @ViewChild('table')
  table: ElementRef;

  lastSelectedArchivedTrackId = -1;

  archivedTracksObservable$: BehaviorSubject<ArchivedTrack[]> = new BehaviorSubject([]);

  subscription = new Subscription();

  predicate: SortPredicate = 'archivedAt';
  reverse = false;

  trackOpen: ArchivedTrack = null;

  hasScroll = false;
  lastArrow = '';
  selectedWithShift: number[] = [];

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly modalService: LibraryModalService,
    private readonly dialog: MatDialog,
    private readonly platform: Platform,
    private readonly hotkeys: HotkeysService,
  ) {
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(SelectArchivedTrack),
          map(acc => acc.archivedTrack.id),
        )
        .subscribe(idArchivedTrack => {
          this.lastSelectedArchivedTrackId = idArchivedTrack;
        }),
    );

    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(UnselectArchivedTrack),
          map(acc => acc.archivedTrack.id),
        )
        .subscribe(idArchivedTrack => {
          if (this.lastSelectedArchivedTrackId === idArchivedTrack) {
            this.lastSelectedArchivedTrackId = -1;
          }
        }),
    );

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(UnselectAll)).subscribe(() => {
        this.lastSelectedArchivedTrackId = -1;
      }),
    );

    this.subscription.add(
      fromEvent(window, 'resize')
        .pipe(throttleTime(250))
        .subscribe(() => this.onWindowResize()),
    );
  }

  ngAfterViewInit(): void {
    this.detectScroll();
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(ToggleSelectArchivedTrack))
        .subscribe(({ clicked }: ToggleSelectArchivedTrack) => {
          if (clicked) {
            this.selectedWithShift = [];
          }
        }),
    );

    // Hotkeys
    this.subscription.add(
      this.hotkeys.fromConfigArray([
        { hotkey: 'delete', handler: () => this.onDeleteHotkey() },
        { hotkey: 'selectAll', handler: () => this.onSelectAllHotkey() },
        { hotkey: 'selectUp', handler: e => this.onSelectUpDownHotkey(e) },
        { hotkey: 'selectDown', handler: e => this.onSelectUpDownHotkey(e) },
        {
          hotkey: 'selectUpWithSelection',
          handler: e => this.onSelectUpDownWithSelectionHotkey(e),
        },
        {
          hotkey: 'selectDownWithSelection',
          handler: e => this.onSelectUpDownWithSelectionHotkey(e),
        },
        { hotkey: 'playPause', handler: () => this.onPlayPauseHotkey() },
        { hotkey: 'cancel', handler: () => this.onCancelHotkey() },
      ]),
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ResetEditingTracks());
    this.subscription.unsubscribe();
  }

  onWindowResize(): void {
    this.detectScroll();
  }

  onSelectShiftLine(idArchivedTrackTriggered: number) {
    if (this.lastSelectedArchivedTrackId === -1) {
      return;
    }
    this.archivedTracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe(archivedTracks => {
        const startIdx = archivedTracks.findIndex(
          archivedTrack => archivedTrack.id === this.lastSelectedArchivedTrackId,
        );
        const endIdx = archivedTracks.findIndex(
          archivedTrack => archivedTrack.id === idArchivedTrackTriggered,
        );
        if (endIdx === startIdx) {
          return;
        }
        let ids: ArchivedTrack[];
        if (endIdx < startIdx) {
          ids = archivedTracks.slice(endIdx, startIdx + 1);
        } else {
          ids = archivedTracks.slice(startIdx, endIdx + 1);
        }
        this.store.dispatch(
          new SelectMultipleArchivedTracks(ids.filter(tra => !tra.processing)),
        );
        this.lastSelectedArchivedTrackId = idArchivedTrackTriggered;
      });
  }

  transition() {
    this.sortAll();
  }

  sortAll() {
    this.sortBy(this.predicate, !this.reverse);
  }

  sortBy(prop: SortPredicate, revert = false) {
    // We do two case since if we add a if in the sort function, we loose performance

    this.archivedTracksObservable$.next(this.getSortedArray(prop, revert));
  }

  getSortedArray(prop: SortPredicate, revert = false): ArchivedTrack[] {
    if (prop === 'none') {
      return this.archivedTracks;
    }
    if (['archivedAt', 'uploadDate'].includes(prop)) {
      if (revert) {
        return this.archivedTracks.slice().sort(sortDateReverse(prop));
      }

      return this.archivedTracks.slice().sort(sortDate(prop));
    }
    if (['title', 'artist', 'album'].includes(prop)) {
      if (revert) {
        return this.archivedTracks.slice().sort(sortStringReverse(prop));
      }

      return this.archivedTracks.slice().sort(sortString(prop));
    }
    if (revert) {
      return this.archivedTracks.slice().sort(sortNumberReverse(prop));
    }

    return this.archivedTracks.slice().sort(sortNumber(prop));
  }

  completedArchivedTracksLength(archivedTracks: ArchivedTrack[]): number {
    return archivedTracks.filter(archivedTrack => !archivedTrack.processing).length;
  }

  getTableHeight(): number {
    // Size * line height + header height
    if (this.archivedTracksObservable$.value.length) {
      return this.archivedTracksObservable$.value.length * 50 + 43;
    }

    return 256;
  }

  detectScroll() {
    if (this.table && !this.platform.FIREFOX) {
      const totalTableHeight = 219 + this.getTableHeight();
      this.hasScroll = totalTableHeight > window.innerHeight;
    }
  }

  getMultiSelectButtonStyle(): any {
    return {
      padding: '0 10px',
      height: '18px',
    };
  }

  toggleAll(e: MatCheckboxChange, force = false) {
    if (force || e.checked) {
      this.store.dispatch(new SelectMultipleArchivedTracks(this.archivedTracks));
    } else {
      this.store.dispatch(new UnselectAll());
    }
  }

  isMultiSelect(id: number) {
    const selectedArchivedTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedArchivedTracks,
    );

    return (
      selectedArchivedTracks.length > 1 &&
      selectedArchivedTracks.findIndex(t => t === id) > -1
    );
  }

  isNotLocked(archivedTrack: ArchivedTrack) {
    return !archivedTrack.locked;
  }

  playTrack(archivedTrack: ArchivedTrack) {
    this.currentTrackPlaying$.pipe(take(1)).subscribe(currentTrackPlaying => {
      if (currentTrackPlaying?.id !== archivedTrack.id) {
        this.store.dispatch(new PlayAudio(archivedTrack));
      }
    });
  }

  deleteArchivedTrack(archivedTrack: ArchivedTrack) {
    this.modalService.deleteArchivedTrack(archivedTrack);
  }

  deleteMultiple() {
    this.modalService.deleteMultipleArchivedTracks(
      this.store.selectSnapshot(SelectedTracksState.selectedArchivedTracks),
    );
  }

  restoreArchivedTrack(archivedTrack: ArchivedTrack) {
    this.store.dispatch(new ArchivedTrackRestoreRequest([archivedTrack.id]));
  }

  restoreMultiple() {
    this.store.dispatch(
      new ArchivedTrackRestoreRequest(
        this.store.selectSnapshot(SelectedTracksState.selectedArchivedTracks),
      ),
    );
  }

  onOpenContextMenu(archivedTrack: ArchivedTrack) {
    this.trackOpen = archivedTrack;
  }

  onCloseContextMenu() {
    this.trackOpen = null;
  }

  onDeleteHotkey() {
    if (this.trackOpen && !this.actionMultipleTrack()) {
      this.deleteArchivedTrack(this.trackOpen);
    } else if (
      this.store.selectSnapshot(SelectedTracksState.selectedArchivedTracks).length
    ) {
      this.deleteMultiple();
    }
  }

  onSelectAllHotkey() {
    this.toggleAll(null, true);
  }

  onSelectUpDownHotkey(e: KeyboardEvent) {
    if (this.lastSelectedArchivedTrackId === -1) {
      if (e.key === 'ArrowDown') {
        this.selectFirstTrack();
      }

      return;
    }
    this.selectNextTrack(e.key);
  }

  onSelectUpDownWithSelectionHotkey(e: KeyboardEvent) {
    if (this.lastSelectedArchivedTrackId === -1) {
      return;
    }
    this.selectNextTrack(e.key, true);
  }

  selectFirstTrack() {
    this.archivedTracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe((tracks: ArchivedTrack[]) => {
        if (!tracks.length) {
          return;
        }
        const focusedTrack = tracks[0];
        this.store.dispatch(new SelectArchivedTrack(focusedTrack));
      });
  }

  selectNextTrack(key: string, shiftPressed = false) {
    this.archivedTracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe((tracks: ArchivedTrack[]) => {
        const startIdx = tracks.findIndex(
          track => track.id === this.lastSelectedArchivedTrackId,
        );
        let newIdx =
          this.lastArrow !== '' && key !== this.lastArrow && shiftPressed
            ? startIdx
            : startIdx + (key === 'ArrowDown' ? 1 : -1);
        if (newIdx < 0 || newIdx >= tracks.length) {
          this.checkTableScroll(newIdx, key);

          return;
        }
        let newFocusedTrack = tracks[newIdx];

        if (!shiftPressed) {
          this.store.dispatch(new UnselectAll());
          this.store.dispatch(new SelectArchivedTrack(newFocusedTrack, true));
          this.lastArrow = '';
          this.selectedWithShift = [];
          this.checkTableScroll(newIdx, key);

          return;
        }

        if (
          newFocusedTrack.id ===
          this.store.selectSnapshot(SelectedTracksState.lastClicked)
        ) {
          newIdx = newIdx + (key === 'ArrowDown' ? 1 : -1);
          newIdx = Math.min(tracks.length - 1, Math.max(0, newIdx));
          newFocusedTrack = tracks[newIdx];
        }
        this.checkTableScroll(newIdx, key);

        if (this.selectedWithShift.indexOf(newFocusedTrack.id) > -1) {
          this.store.dispatch(new UnselectArchivedTrack(newFocusedTrack));
          this.selectedWithShift = this.selectedWithShift.filter(
            id => id !== newFocusedTrack.id,
          );
        } else {
          this.store.dispatch(new SelectArchivedTrack(newFocusedTrack));
          this.selectedWithShift.push(newFocusedTrack.id);
        }
        this.lastSelectedArchivedTrackId = newFocusedTrack.id;
        this.lastArrow = key;
      });
  }

  checkTableScroll(idx: number, key: string) {
    const linesDisplayed = Math.floor(
      this.cdkTable.elementRef.nativeElement.offsetHeight / 50,
    );
    if (
      key === 'ArrowDown' &&
      (idx + 1) * 50 > this.cdkTable.measureScrollOffset('top') + linesDisplayed * 50
    ) {
      this.cdkTable.scrollToIndex(1 + idx - linesDisplayed);
    } else if (this.cdkTable.measureScrollOffset('top') > idx * 50) {
      this.cdkTable.scrollToIndex(idx);
    }
  }

  onPlayPauseHotkey() {
    if (this.dialog.getDialogById(MIXPOINT_MODAL_ID)) {
      return;
    }
    amplitude.getInstance().logEvent('listen_track_shortcut');
    if (this.trackOpen) {
      this.playTrack(this.trackOpen);

      return;
    }
    const selectedIds = this.store.selectSnapshot(
      SelectedTracksState.selectedArchivedTracks,
    );
    if (selectedIds.length === 1) {
      const selectedTrack = this.store.selectSnapshot(TrashState.archivedTrackWithId)(
        selectedIds[0],
      );
      this.playTrack(selectedTrack);
    }
  }

  onCancelHotkey() {
    this.store.dispatch(new UnselectAll());
  }

  actionMultipleTrack() {
    const selectedTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedArchivedTracks,
    );

    return (
      !this.trackOpen ||
      (selectedTracks.length > 1 &&
        selectedTracks.filter(id => id === this.trackOpen.id).length)
    );
  }
}
